import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  FormLabel,
  MenuItem,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useCallback, useMemo, useState, type ChangeEvent } from "react";
import {
  type Edi214ShipmentStatus,
  EdiOutboundPayloadDestination,
  type TenediPartnershipFieldsFragment,
} from "../../../generated/graphql";
import {
  DESTINATION_LABELS,
  PARTNERSHIP_FIELD_COPY,
  PRODUCTION_SAVE_HINT,
} from "../tenedi-connection-copy";
import {
  formErrors,
  needsCutoverConfirmation,
  newPartnershipForm,
  partnershipFormFromEntity,
  partnershipFormSchema,
  type PartnershipFormValues,
} from "../tenedi-connection-forms";
import Edi214CheckboxList from "./edi-214-checkbox-list";
import IdentityTextField from "./identity-text-field";
import SettingSwitch from "./setting-switch";

const styles = {
  section: { mt: 3 } satisfies SxProps<Theme>,
  row: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 2,
    mt: 1,
  } satisfies SxProps<Theme>,
  readOnly: { fontFamily: "monospace" } satisfies SxProps<Theme>,
};

const DESTINATIONS = Object.values(EdiOutboundPayloadDestination);

const DESTINATION_BY_VALUE = new Map(
  DESTINATIONS.map((destination) => [String(destination), destination]),
);

type BooleanField = {
  [Key in keyof PartnershipFormValues]: PartnershipFormValues[Key] extends boolean
    ? Key
    : never;
}[keyof PartnershipFormValues];

const REPLY_FIELDS = [
  "autoAcceptOrders",
  "send990InResponseTo204",
  "send990InResponseTo211",
] as const satisfies readonly BooleanField[];

const INBOUND_FIELDS = [
  "canCreateMultipleStops",
  "ignoreCancellations",
  "allowEditingShipperBillOfLadingNumber",
] as const satisfies readonly BooleanField[];

const OUTBOUND_FIELDS = [
  "ignoreNonEdiOutbound",
  "removeSpacesFrom214Bol",
  "excludeZeroChargeInvoices",
  "sendBackupDocs",
] as const satisfies readonly BooleanField[];

const ADVANCED_FIELDS = [
  "enableStaging",
  "useStediGenerateEdiFunction",
] as const satisfies readonly BooleanField[];

const STEDI_READ_ONLY_FIELDS = [
  "stediPartnershipId",
  "stedi990MappingId",
  "stedi990TransactionSettingId",
  "stedi214MappingId",
  "stedi214TransactionSettingId",
  "stedi210MappingId",
  "stedi210TransactionSettingId",
] as const satisfies ReadonlyArray<keyof TenediPartnershipFieldsFragment>;

export type PartnershipSave = {
  readonly values: PartnershipFormValues;
  readonly confirmCutover: boolean;
};

type PartnershipDialogProps = {
  /** The partnership being edited, or null to add one. */
  readonly original: TenediPartnershipFieldsFragment | null;
  readonly carrierScacs: readonly string[];
  readonly saveDisabled: boolean;
  readonly onClose: () => void;
  /** Saves the partnership; a rejection's message shows in the dialog. */
  readonly onSave: (save: PartnershipSave) => Promise<void>;
};

/**
 * Adds or edits one partnership. It validates the way the backend does before
 * saving, and asks for the cutover confirmation when an existing
 * partnership's destination changes. The page mounts it only while open, so
 * each opening starts from the partnership as stored.
 */
const PartnershipDialog = ({
  original,
  carrierScacs,
  saveDisabled,
  onClose,
  onSave,
}: PartnershipDialogProps) => {
  const [values, setValues] = useState<PartnershipFormValues>(() =>
    original === null
      ? newPartnershipForm(carrierScacs)
      : partnershipFormFromEntity(original),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmCutover, setConfirmCutover] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const schema = useMemo(
    () => partnershipFormSchema(carrierScacs),
    [carrierScacs],
  );
  const cutover = needsCutoverConfirmation(original, values);
  const stediIds = useMemo(
    () =>
      original === null
        ? []
        : STEDI_READ_ONLY_FIELDS.flatMap((field) => {
            const value = original[field];
            return value === null || value === undefined
              ? []
              : [{ field, value }];
          }),
    [original],
  );

  const handleSwitch = useCallback((name: BooleanField, checked: boolean) => {
    setValues((previous) => ({ ...previous, [name]: checked }));
  }, []);
  const handleStatuses = useCallback((next: Edi214ShipmentStatus[]) => {
    setValues((previous) => ({ ...previous, edi214ShipmentStatuses: next }));
  }, []);
  const handleText = useCallback(
    (name: "alternateStediPartnerId", value: string) => {
      setValues((previous) => ({ ...previous, [name]: value }));
    },
    [],
  );
  const handleScac = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValues((previous) => ({
        ...previous,
        companyStandardCarrierAlphaCode: event.target.value,
      }));
    },
    [],
  );
  const handleDestination = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const next = DESTINATION_BY_VALUE.get(event.target.value);
      if (next !== undefined) {
        setValues((previous) => ({
          ...previous,
          ediOutboundPayloadDestination: next,
        }));
        setConfirmCutover(false);
      }
    },
    [],
  );
  const handleCutover = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setConfirmCutover(event.target.checked);
  }, []);

  const handleSave = useCallback(() => {
    const found = formErrors(schema, values);
    setErrors(found);
    setSaveError(null);
    if (Object.keys(found).length > 0) {
      return;
    }
    setSaving(true);
    onSave({ values, confirmCutover: cutover && confirmCutover })
      .catch((error: unknown) => {
        setSaveError(error instanceof Error ? error.message : String(error));
      })
      .finally(() => {
        setSaving(false);
      });
  }, [confirmCutover, cutover, onSave, schema, values]);

  const handleDialogClose = useCallback(() => {
    if (!saving) {
      onClose();
    }
  }, [onClose, saving]);

  const renderSwitch = (name: BooleanField) => (
    <SettingSwitch
      key={name}
      checked={values[name]}
      copy={PARTNERSHIP_FIELD_COPY[name]}
      disabled={saving}
      name={name}
      onChange={handleSwitch}
    />
  );

  return (
    <Dialog fullWidth open maxWidth="md" onClose={handleDialogClose}>
      <DialogTitle>
        {original === null ? "Add Tenedi partnership" : "Edit partnership"}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={styles.row}>
          <TextField
            select
            disabled={saving}
            error={errors.companyStandardCarrierAlphaCode !== undefined}
            helperText={
              errors.companyStandardCarrierAlphaCode ??
              PARTNERSHIP_FIELD_COPY.companyStandardCarrierAlphaCode.helper
            }
            label={PARTNERSHIP_FIELD_COPY.companyStandardCarrierAlphaCode.label}
            size="small"
            value={values.companyStandardCarrierAlphaCode}
            onChange={handleScac}
          >
            {carrierScacs.map((scac) => (
              <MenuItem key={scac} value={scac}>
                {scac}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            disabled={saving}
            helperText={
              PARTNERSHIP_FIELD_COPY.ediOutboundPayloadDestination.helper
            }
            label={PARTNERSHIP_FIELD_COPY.ediOutboundPayloadDestination.label}
            size="small"
            value={values.ediOutboundPayloadDestination}
            onChange={handleDestination}
          >
            {DESTINATIONS.map((destination) => (
              <MenuItem key={destination} value={destination}>
                {DESTINATION_LABELS[destination]}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {cutover && original !== null && (
          <Alert severity="warning" sx={styles.section}>
            This partnership sends to{" "}
            {DESTINATION_LABELS[original.ediOutboundPayloadDestination]} today.
            Saving switches its next message to{" "}
            {DESTINATION_LABELS[values.ediOutboundPayloadDestination]} straight
            away. Check the new destination is live for this partner first.
            <FormControlLabel
              control={
                <Checkbox checked={confirmCutover} onChange={handleCutover} />
              }
              label="I understand. Switch where this partnership sends."
            />
          </Alert>
        )}
        <FormGroup sx={styles.section}>
          <FormLabel>Tenders and 990 replies</FormLabel>
          {REPLY_FIELDS.map((name) => renderSwitch(name))}
        </FormGroup>
        <Box sx={styles.section}>
          <Edi214CheckboxList
            disabled={saving}
            error={errors.edi214ShipmentStatuses}
            value={values.edi214ShipmentStatuses}
            onChange={handleStatuses}
          />
        </Box>
        <FormGroup sx={styles.section}>
          <FormLabel>Incoming tenders</FormLabel>
          {INBOUND_FIELDS.map((name) => renderSwitch(name))}
        </FormGroup>
        <FormGroup sx={styles.section}>
          <FormLabel>Outgoing messages</FormLabel>
          {OUTBOUND_FIELDS.map((name) => renderSwitch(name))}
        </FormGroup>
        <Accordion disableGutters sx={styles.section} variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Advanced</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormGroup>
              {ADVANCED_FIELDS.map((name) => renderSwitch(name))}
            </FormGroup>
            <IdentityTextField
              copy={PARTNERSHIP_FIELD_COPY.alternateStediPartnerId}
              disabled={saving}
              error={errors.alternateStediPartnerId}
              name="alternateStediPartnerId"
              value={values.alternateStediPartnerId}
              onChange={handleText}
            />
            {stediIds.length > 0 && (
              <Box sx={styles.section}>
                <FormLabel>Stedi settings (read only)</FormLabel>
                <Typography color="text.secondary" variant="body2">
                  Engineering manages these. Some Kuehne+Nagel flows depend on
                  the Stedi partnership ID.
                </Typography>
                {stediIds.map(({ field, value }) => (
                  <Typography key={field} sx={styles.readOnly} variant="body2">
                    {field}: {value}
                  </Typography>
                ))}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
        {saveError !== null && (
          <Alert severity="error" sx={styles.section}>
            {saveError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {saveDisabled && (
          <Typography color="error" variant="body2">
            {PRODUCTION_SAVE_HINT}
          </Typography>
        )}
        <Button disabled={saving} onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={saving || saveDisabled || (cutover && !confirmCutover)}
          variant="contained"
          onClick={handleSave}
        >
          {saving ? "Saving…" : "Save partnership"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartnershipDialog;
